import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getFunnelData, FunnelData, getConstellationQRData, getExperienceTimeData, ExperienceTimeData, getBackgroundSelectionData, BackgroundSelectionData, getQuizProgressData, QuizProgressData, getDropoutData, DropoutData, getMessageSendCountData, MessageSendCountData } from '../utils/funnelDataUtils';

const Container = styled.div`
  padding: 24px;
  width: 100%;
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f8fafc;
  min-height: 100vh;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  color: #1a202c;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: #718096;
  font-size: 1.1rem;
  margin: 0;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
`;

const BottomRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 24px;
`;

const DateRangeSelector = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
`;

const DateRangeTitle = styled.h3`
  margin: 0 0 16px 0;
  color: #2d3748;
  font-size: 1.2rem;
  font-weight: 600;
`;

const BackgroundSelectionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;

  .card-header {
    margin-bottom: 20px;
  }

  .card-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 4px;
  }

  .card-subtitle {
    font-size: 0.9rem;
    color: #718096;
  }

  .table-container {
    max-height: 500px;
    overflow-y: auto;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }

  .ranking-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }

  .ranking-table thead {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .ranking-table th {
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: #4a5568;
    border-bottom: 2px solid #e2e8f0;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .ranking-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  .ranking-table tr:hover {
    background: #f8fafc;
  }

  .ranking-table tr.top-three {
    background: linear-gradient(90deg, #fff7ed, #fef3c7);
  }

  .rank-cell {
    width: 60px;
    text-align: center;
  }

  .rank-badge {
    display: inline-block;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    line-height: 24px;
    text-align: center;
    font-weight: 700;
    font-size: 0.8rem;
    background: #e2e8f0;
    color: #718096;
  }

  .rank-badge.special {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  }

  .name-cell {
    font-weight: 500;
    color: #2d3748;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .count-cell {
    text-align: center;
    font-weight: 600;
    color: #4a5568;
    width: 80px;
  }

  .percentage-cell {
    width: 120px;
  }

  .percentage-bar {
    position: relative;
    height: 20px;
    background: #f1f5f9;
    border-radius: 10px;
    overflow: hidden;
  }

  .percentage-fill {
    height: 100%;
    background: linear-gradient(90deg, #667eea, #764ba2);
    border-radius: 10px;
    transition: width 0.3s ease;
  }

  .percentage-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 0.75rem;
    font-weight: 600;
    color: #4a5568;
    z-index: 1;
  }
`;


const DateInputs = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const DateInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const LoadButton = styled.button`
  padding: 12px 24px;
  background: #3182ce;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2c5aa0;
  }
`;

const MetricCard = styled.div<{ color: string }>`
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  
  .metric-header {
    margin-bottom: 12px;
  }
  
  .metric-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    line-height: 1.2;
  }
  
  .metric-value {
    font-size: 1.8rem;
    font-weight: 700;
    color: ${props => props.color};
    margin: 6px 0;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .metric-description {
    color: #718096;
    font-size: 0.75rem;
    margin-bottom: 12px;
    line-height: 1.3;
  }
  
  .metric-details {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 0.75rem;
    
    .detail-item {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      border-bottom: 1px solid #f1f5f9;
      
      &:last-child {
        border-bottom: none;
      }
      
      .label {
        color: #718096;
        font-weight: 500;
      }
      
      .value {
        color: #2d3748;
        font-weight: 600;
      }
    }
  }
`;

const QRCombinedCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
  
  .card-header {
    margin-bottom: 24px;
  }
  
  .card-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
  }
  
  .qr-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
  
  .qr-item {
    background: #f7fafc;
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #e2e8f0;
    
    .qr-name {
      font-size: 1rem;
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 8px;
    }
    
    .qr-value {
      font-size: 2rem;
      font-weight: 700;
      color: #667eea;
      margin-bottom: 8px;
    }
    
    .qr-conversion {
      font-size: 0.9rem;
      color: #718096;
      
      .conversion-label {
        margin-bottom: 4px;
      }
      
      .conversion-value {
        font-weight: 600;
        color: #38a169;
      }
    }
  }
`;

const ExperienceTimeCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
  
  .card-header {
    margin-bottom: 24px;
  }
  
  .card-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
  }
  
  .time-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
  
  .time-item {
    background: #f7fafc;
    border-radius: 12px;
    padding: 16px;
    text-align: center;
    border: 1px solid #e2e8f0;
    
    .time-label {
      font-size: 0.9rem;
      color: #718096;
      margin-bottom: 8px;
    }
    
    .time-value {
      font-size: 1.5rem;
      font-weight: 700;
      color: #667eea;
    }
  }
`;

const QuizProgressCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
  grid-column: span 2;
  
  .card-header {
    margin-bottom: 24px;
  }
  
  .card-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
  }
  
  .quiz-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
  }
  
  .quiz-item {
    background: #f7fafc;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #e2e8f0;
    
    .quiz-title {
      font-size: 1rem;
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 12px;
    }
    
    .quiz-stats {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      font-size: 0.85rem;
      
      .stat-item {
        display: flex;
        justify-content: space-between;
        
        .label {
          color: #718096;
        }
        
        .value {
          color: #2d3748;
          font-weight: 600;
        }
      }
    }
    
    .quiz-rates {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #e2e8f0;
      font-size: 0.85rem;
      
      .rate-item {
        display: flex;
        justify-content: space-between;
        margin-bottom: 4px;
        
        .label {
          color: #718096;
        }
        
        .value {
          font-weight: 600;
          
          &.correct {
            color: #38a169;
          }
          
          &.incorrect {
            color: #e53e3e;
          }
          
          &.completion {
            color: #3182ce;
          }
        }
      }
    }
  }
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #feb2b2;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #718096;
  font-size: 1.2rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #718096;
  font-size: 1.1rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const FunnelDashboardPage: React.FC = () => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [funnelData, setFunnelData] = useState<FunnelData | null>(null);
  const [constellationQRData, setConstellationQRData] = useState<FunnelData | null>(null);
  const [experienceTimeData, setExperienceTimeData] = useState<ExperienceTimeData[]>([]);
  const [backgroundSelectionData, setBackgroundSelectionData] = useState<BackgroundSelectionData | null>(null);
  const [quizProgressData, setQuizProgressData] = useState<QuizProgressData | null>(null);
  const [dropoutData, setDropoutData] = useState<DropoutData | null>(null);
  const [messageSendCountData, setMessageSendCountData] = useState<MessageSendCountData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maxExperienceTime, setMaxExperienceTime] = useState<number>(0);
  const [minExperienceTime, setMinExperienceTime] = useState<number>(0);
  

  // 기본값 설정 (오늘 날짜)
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
  }, []);

  // 날짜가 설정되면 자동으로 데이터 로드
  useEffect(() => {
    if (startDate && endDate) {
      loadFunnelData();
    }
  }, [startDate, endDate]);

  const loadFunnelData = async () => {
    if (!startDate || !endDate) {
      setError('시작일과 종료일을 모두 선택해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 날짜 범위에 따른 데이터 로드
      const promises = [];
      
      // 각 날짜별로 데이터 수집
      const start = new Date(startDate);
      const end = new Date(endDate);
      const dates = [];
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]);
      }

      // 모든 날짜의 데이터를 병렬로 로드
      for (const date of dates) {
        promises.push(
          getFunnelData(date),
          getConstellationQRData(date),
          getExperienceTimeData(date),
          getBackgroundSelectionData(date),
          getQuizProgressData(date),
          getDropoutData(date),
          getMessageSendCountData(date)
        );
      }

      const results = await Promise.all(promises);
      
      // 데이터 집계
      let totalPreshowQR = 0;
      let totalConstellationQR = 0;
      let totalExperienceTime = 0;
      let tempMinExperienceTime = Infinity;
      let tempMaxExperienceTime = 0;
      let totalRegisteredUsers = 0;
      let totalMessageSentUsers = 0;
      let totalEndingReachedUsers = 0;
      let totalMessageSends = 0;
      let combinedBackgroundSelections: Record<string, number> = {};
      let combinedQuizProgress: any = {
        quiz1: { entries: 0, correct: 0, incorrect: 0 },
        quiz2: { entries: 0, correct: 0, incorrect: 0 },
        quiz3: { entries: 0, correct: 0, incorrect: 0 },
        quiz4: { entries: 0, correct: 0, incorrect: 0 },
        quiz5: { entries: 0, correct: 0, incorrect: 0 },
        quiz6: { entries: 0, correct: 0, incorrect: 0 }
      };

      // 결과 처리
      for (let i = 0; i < results.length; i += 7) {
        const preshowData = results[i] as FunnelData | null;
        const constellationData = results[i + 1] as FunnelData | null;
        const experienceData = results[i + 2] as ExperienceTimeData[] | null;
        const backgroundData = results[i + 3] as BackgroundSelectionData | null;
        const quizData = results[i + 4] as QuizProgressData | null;
        const dropoutData = results[i + 5] as DropoutData | null;
        const messageSendCountData = results[i + 6] as MessageSendCountData | null;

        if (preshowData) totalPreshowQR += preshowData.qrEntries;
        if (constellationData) totalConstellationQR += constellationData.qrEntries;
        
        if (experienceData && experienceData.length > 0) {
          experienceData.forEach(exp => {
            totalExperienceTime += exp.experienceDuration;
            tempMinExperienceTime = Math.min(tempMinExperienceTime, exp.experienceDuration);
            tempMaxExperienceTime = Math.max(tempMaxExperienceTime, exp.experienceDuration);
          });
        }

        if (backgroundData) {
          Object.entries(backgroundData.selections).forEach(([key, value]) => {
            combinedBackgroundSelections[key] = (combinedBackgroundSelections[key] || 0) + value;
          });
        }

        if (quizData) {
          Object.entries(quizData.quizzes).forEach(([quizKey, quizData]) => {
            combinedQuizProgress[quizKey].entries += quizData.entries;
            combinedQuizProgress[quizKey].correct += quizData.correct;
            combinedQuizProgress[quizKey].incorrect += quizData.incorrect;
          });
        }

        if (dropoutData) {
          totalRegisteredUsers += dropoutData.registeredUsers || 0;
          totalMessageSentUsers += dropoutData.messageSentUsers || 0;
          totalEndingReachedUsers += dropoutData.endingReachedUsers || 0;
        }

        if (messageSendCountData) {
          totalMessageSends += messageSendCountData.totalMessageSends || 0;
        }
      }

      // 집계된 데이터 설정
      setFunnelData({ date: `${startDate}~${endDate}`, qrEntries: totalPreshowQR, lastUpdated: new Date() as any });
      setConstellationQRData({ date: `${startDate}~${endDate}`, qrEntries: totalConstellationQR, lastUpdated: new Date() as any });
      setExperienceTimeData([{
        avatarRegistrationTime: new Date() as any,
        messageSentTime: new Date() as any,
        experienceDuration: totalExperienceTime,
        date: `${startDate}~${endDate}`,
        lastUpdated: new Date() as any
      }]);
      setBackgroundSelectionData({
        date: `${startDate}~${endDate}`,
        selections: combinedBackgroundSelections,
        totalSelections: Object.values(combinedBackgroundSelections).reduce((sum, val) => sum + val, 0),
        lastUpdated: new Date() as any
      });
      setQuizProgressData({
        date: `${startDate}~${endDate}`,
        quizzes: combinedQuizProgress,
        lastUpdated: new Date() as any
      });
      setDropoutData({
        date: `${startDate}~${endDate}`,
        registeredUsers: totalRegisteredUsers,
        messageSentUsers: totalMessageSentUsers,
        endingReachedUsers: totalEndingReachedUsers,
        dropoutRate: totalRegisteredUsers > 0 ? ((totalRegisteredUsers - totalMessageSentUsers) / totalRegisteredUsers * 100) : 0,
        completionRate: totalRegisteredUsers > 0 ? (totalEndingReachedUsers / totalRegisteredUsers * 100) : 0,
        lastUpdated: new Date() as any
      });
      setMessageSendCountData({
        date: `${startDate}~${endDate}`,
        totalMessageSends: totalMessageSends,
        lastUpdated: new Date() as any
      });

      // 체험 시간 관련 state 설정
      setMaxExperienceTime(tempMaxExperienceTime);
      setMinExperienceTime(tempMinExperienceTime === Infinity ? 0 : tempMinExperienceTime);

    } catch (err) {
      setError('퍼널 데이터를 불러오는 중 오류가 발생했습니다.');
      console.error('Funnel data load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadData = () => {
    loadFunnelData();
  };

  const formatDuration = (milliseconds: number): string => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}시간 ${minutes}분 ${seconds}초`;
    } else if (minutes > 0) {
      return `${minutes}분 ${seconds}초`;
    } else {
      return `${seconds}초`;
    }
  };

  // 전체 오프라인 방문자 수 (임시로 1000명으로 설정 - 실제로는 별도 데이터에서 가져와야 함)
  const totalOfflineVisitors = 1000;


  return (
    <Container>
      <ContentWrapper>
        <Header>
          <Title>퍼널 데이터 분석 대시보드</Title>
        </Header>

        <DateRangeSelector>
        <DateRangeTitle>분석 기간 설정</DateRangeTitle>
        <DateInputs>
          <DateInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="시작일"
          />
          <span style={{ color: '#718096', fontWeight: '600' }}>~</span>
          <DateInput
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="종료일"
          />
          <LoadButton onClick={handleLoadData}>
            데이터 분석
          </LoadButton>
        </DateInputs>
      </DateRangeSelector>


      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading && <LoadingMessage>데이터를 분석하는 중...</LoadingMessage>}

      {!loading && !error && (funnelData || constellationQRData || experienceTimeData.length > 0 || backgroundSelectionData || quizProgressData || dropoutData) && (
        <>
          <TopRow>
            {/* QR 진입 건수 통합 카드 */}
            {(funnelData || constellationQRData) && (
              <QRCombinedCard>
                <div className="card-header">
                  <div className="card-title">QR 진입 분석</div>
                </div>
              <div className="qr-grid">
                <div className="qr-item">
                  <div className="qr-name">프리쇼 QR</div>
                  <div className="qr-value">{funnelData?.qrEntries || 0}</div>
                  <div className="qr-conversion">
                    <div className="conversion-label">전환율</div>
                    <div className="conversion-value">
                      {totalOfflineVisitors > 0 
                        ? (((funnelData?.qrEntries || 0) / totalOfflineVisitors) * 100).toFixed(1)
                        : '0.0'
                      }%
                    </div>
                  </div>
                </div>
                <div className="qr-item">
                  <div className="qr-name">별자리연회 QR</div>
                  <div className="qr-value">{constellationQRData?.qrEntries || 0}</div>
                  <div className="qr-conversion">
                    <div className="conversion-label">전환율</div>
                    <div className="conversion-value">
                      {totalOfflineVisitors > 0 
                        ? (((constellationQRData?.qrEntries || 0) / totalOfflineVisitors) * 100).toFixed(1)
                        : '0.0'
                      }%
                    </div>
                  </div>
                </div>
              </div>
            </QRCombinedCard>
            )}

            {/* 체험 시간 데이터 */}
            {experienceTimeData.length > 0 && (
              <ExperienceTimeCard>
                <div className="card-header">
                  <div className="card-title">체험 시간 분석</div>
                </div>
              <div className="time-grid">
                <div className="time-item">
                  <div className="time-label">총 체험 시간</div>
                  <div className="time-value">{formatDuration(experienceTimeData[0]?.experienceDuration || 0)}</div>
                </div>
                <div className="time-item">
                  <div className="time-label">최장 체험 시간</div>
                  <div className="time-value">{formatDuration(maxExperienceTime || 0)}</div>
                </div>
                <div className="time-item">
                  <div className="time-label">최단 체험 시간</div>
                  <div className="time-value">{formatDuration(minExperienceTime === Infinity ? 0 : minExperienceTime)}</div>
                </div>
                <div className="time-item">
                  <div className="time-label">평균 체험 시간</div>
                  <div className="time-value">
                    {funnelData?.qrEntries && funnelData.qrEntries > 0 
                      ? formatDuration((experienceTimeData[0]?.experienceDuration || 0) / funnelData.qrEntries)
                      : '0초'
                    }
                  </div>
                </div>
              </div>
            </ExperienceTimeCard>
            )}
          </TopRow>

          {/* 배후성 선택 데이터 */}
          {backgroundSelectionData && (
            <BackgroundSelectionCard>
              <div className="card-header">
                <div className="card-title">배후성 선택 분석</div>
                <div className="card-subtitle">인기 배후성 순위</div>
              </div>
              <div className="table-container">
                <table className="ranking-table">
                  <thead>
                    <tr>
                      <th>순위</th>
                      <th>배후성</th>
                      <th>선택 횟수</th>
                      <th>비율</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(backgroundSelectionData.selections)
                      .sort(([,a], [,b]) => b - a)
                      .map(([backgroundName, count], index) => {
                        const totalSelections = Object.values(backgroundSelectionData.selections).reduce((sum, val) => sum + val, 0);
                        const percentage = totalSelections > 0 ? ((count / totalSelections) * 100).toFixed(1) : '0.0';
                        return (
                          <tr key={backgroundName} className={index < 3 ? 'top-three' : ''}>
                            <td className="rank-cell">
                              <span className={`rank-badge ${index < 3 ? 'special' : ''}`}>
                                {index + 1}
                              </span>
                            </td>
                            <td className="name-cell">{backgroundName}</td>
                            <td className="count-cell">{count}</td>
                            <td className="percentage-cell">
                              <div className="percentage-bar">
                                <div 
                                  className="percentage-fill" 
                                  style={{ width: `${percentage}%` }}
                                />
                                <span className="percentage-text">{percentage}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </BackgroundSelectionCard>
          )}

          {/* 퀴즈 진행도 데이터 */}
          {quizProgressData && (
            <QuizProgressCard>
              <div className="card-header">
                <div className="card-title">퀴즈 진행도 분석</div>
              </div>
              <div className="quiz-grid">
                {Object.entries(quizProgressData.quizzes).map(([quizKey, quizData]) => {
                  const quizNumber = quizKey.replace('quiz', '');
                  const totalAttempts = quizData.correct + quizData.incorrect;
                  const completionRate = quizData.entries > 0 
                    ? ((totalAttempts / quizData.entries) * 100).toFixed(1)
                    : '0';
                  const correctRate = totalAttempts > 0
                    ? ((quizData.correct / totalAttempts) * 100).toFixed(1)
                    : '0';
                  const incorrectRate = totalAttempts > 0
                    ? ((quizData.incorrect / totalAttempts) * 100).toFixed(1)
                    : '0';
                  
                  return (
                    <div key={quizKey} className="quiz-item">
                      <div className="quiz-title">퀴즈 {quizNumber}</div>
                      <div className="quiz-stats">
                        <div className="stat-item">
                          <span className="label">진입</span>
                          <span className="value">{quizData.entries}명</span>
                        </div>
                        <div className="stat-item">
                          <span className="label">정답</span>
                          <span className="value">{quizData.correct}명</span>
                        </div>
                        <div className="stat-item">
                          <span className="label">오답</span>
                          <span className="value">{quizData.incorrect}명</span>
                        </div>
                        <div className="stat-item">
                          <span className="label">시도</span>
                          <span className="value">{totalAttempts}명</span>
                        </div>
                      </div>
                      <div className="quiz-rates">
                        <div className="rate-item">
                          <span className="label">완주율</span>
                          <span className="value completion">{completionRate}%</span>
                        </div>
                        <div className="rate-item">
                          <span className="label">정답률</span>
                          <span className="value correct">{correctRate}%</span>
                        </div>
                        <div className="rate-item">
                          <span className="label">오답률</span>
                          <span className="value incorrect">{incorrectRate}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </QuizProgressCard>
          )}

          <BottomRow>
            {/* 체험 중단 포인트 */}
            {dropoutData && (
              <MetricCard color="#6b7280">
                <div className="metric-header">
                  <div className="metric-title">체험 중단 포인트</div>
                </div>
              <div className="metric-value">{(dropoutData.dropoutRate || 0).toFixed(1)}%</div>
              <div className="metric-description">등록 후 메시지를 보내지 않은 유저의 이탈율</div>
              <div className="metric-details">
                <div className="detail-item">
                  <span className="label">등록한 유저</span>
                  <span className="value">{dropoutData.registeredUsers || 0}명</span>
                </div>
                <div className="detail-item">
                  <span className="label">메시지 전송</span>
                  <span className="value">{dropoutData.messageSentUsers || 0}명</span>
                </div>
              </div>
            </MetricCard>
            )}

            {/* 체험 완료율 */}
            {dropoutData && (
              <MetricCard color="#6b7280">
                <div className="metric-header">
                  <div className="metric-title">체험 완료율</div>
                </div>
              <div className="metric-value">{(dropoutData.completionRate || 0).toFixed(1)}%</div>
              <div className="metric-description">진입 대비 엔딩까지 도달한 비율</div>
              <div className="metric-details">
                <div className="detail-item">
                  <span className="label">전체 시작 유저</span>
                  <span className="value">{dropoutData.registeredUsers || 0}명</span>
                </div>
                <div className="detail-item">
                  <span className="label">엔딩 도달</span>
                  <span className="value">{dropoutData.endingReachedUsers || 0}명</span>
                </div>
              </div>
            </MetricCard>
            )}

            {/* 메시지 전송 액션 수행률 */}
            {dropoutData && (
              <MetricCard color="#6b7280">
                <div className="metric-header">
                  <div className="metric-title">메시지 전송 액션 수행률</div>
                </div>
              <div className="metric-value">
                {dropoutData.registeredUsers > 0 
                  ? (((dropoutData.messageSentUsers || 0) / dropoutData.registeredUsers) * 100).toFixed(1)
                  : '0.0'
                }%
              </div>
              <div className="metric-description">등록한 유저 중 메시지를 전송한 유저의 비율</div>
              <div className="metric-details">
                <div className="detail-item">
                  <span className="label">전체 유저</span>
                  <span className="value">{dropoutData.registeredUsers || 0}명</span>
                </div>
                <div className="detail-item">
                  <span className="label">메시지 전송</span>
                  <span className="value">{dropoutData.messageSentUsers || 0}명</span>
                </div>
              </div>
            </MetricCard>
            )}

            {/* 메시지 전송 횟수 분석 */}
            {messageSendCountData && (
              <MetricCard color="#6b7280">
                <div className="metric-header">
                  <div className="metric-title">메시지 전송 횟수</div>
                </div>
                <div className="metric-value">{messageSendCountData.totalMessageSends || 0}회</div>
                <div className="metric-description">총 메시지 전송 횟수</div>
              </MetricCard>
            )}
          </BottomRow>
        </>
      )}

      {!loading && !error && !funnelData && !constellationQRData && experienceTimeData.length === 0 && !backgroundSelectionData && !quizProgressData && !dropoutData && (
        <NoDataMessage>
          선택한 기간의 데이터가 없습니다. 다른 기간을 선택해보세요.
        </NoDataMessage>
        )}
      </ContentWrapper>
    </Container>
  );
};

export default FunnelDashboardPage;